import React, { useEffect, useState } from 'react';
import {
  deleteOfflineEpisode,
  getAllOfflineEpisodes,
  getOfflineEpisodesForAnime,
  getStorageEstimate,
  OfflineEpisode,
} from '../services/animeOfflineDB';
import { formatBytes } from '../services/episodeDownloader';
import { OfflinePlayerModal } from './OfflinePlayerModal';
import { Play, Trash2, HardDrive } from 'lucide-react';

interface OfflineEpisodeListProps {
  /** Only this show's episodes; omitted = every downloaded episode (library view) */
  animeId?: string;
  /** Bump to re-read the list (e.g. after a download finishes) */
  refreshKey?: number;
  onWatched?: (episode: OfflineEpisode) => void;
  /** Hide the whole block when nothing is downloaded */
  hideWhenEmpty?: boolean;
}

export const OfflineEpisodeList: React.FC<OfflineEpisodeListProps> = ({
  animeId,
  refreshKey = 0,
  onWatched,
  hideWhenEmpty,
}) => {
  const [list, setList] = useState<OfflineEpisode[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState<OfflineEpisode | null>(null);
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null);

  const reload = () => {
    (animeId ? getOfflineEpisodesForAnime(animeId) : getAllOfflineEpisodes())
      .then(setList)
      .catch(() => setList([]))
      .finally(() => setLoaded(true));
    getStorageEstimate().then(setStorage);
  };
  useEffect(reload, [animeId, refreshKey]);

  const handleDelete = async (ep: OfflineEpisode) => {
    if (!confirm(`ลบตอนที่ ${ep.number} (${ep.audio}) ของ ${ep.animeTitle} ออกจากเครื่อง?`)) return;
    await deleteOfflineEpisode(ep.id).catch(() => {});
    reload();
  };

  if (!loaded || (hideWhenEmpty && list.length === 0)) return null;

  return (
    <div className="space-y-2">
      {list.length === 0 ? (
        <div className="py-8 text-center text-slate-500 text-xs bg-slate-950/50 rounded-xl border border-slate-800">
          ยังไม่มีตอนที่โหลดเก็บไว้ในแอป — กดการ์ดอนิเมะ แล้วกด "โหลด" ที่ตอนที่ต้องการ
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {list.map((ep) => {
            const complete = ep.savedSegments >= ep.segmentCount;
            const pct = Math.floor((ep.savedSegments / Math.max(1, ep.segmentCount)) * 100);
            return (
              <div
                key={ep.id}
                className="p-2.5 rounded-xl border bg-slate-950/80 border-slate-800 flex items-center justify-between gap-2"
              >
                <div className="min-w-0 flex-1">
                  {!animeId && <div className="text-xs font-bold text-white truncate">{ep.animeTitle}</div>}
                  <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                    <span className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 text-[11px] font-bold">
                      ตอนที่ {ep.number}
                    </span>
                    <span
                      className={`px-1.5 rounded text-[10px] font-bold ${
                        ep.audio === 'พากย์ไทย' ? 'bg-sky-500/20 text-sky-300' : 'bg-purple-500/20 text-purple-300'
                      }`}
                    >
                      {ep.audio}
                    </span>
                    <span className="text-[10px] text-slate-500">{formatBytes(ep.sizeBytes)}</span>
                    {ep.source && <span className="text-[10px] text-slate-500">· {ep.source}</span>}
                  </div>
                  {!complete && (
                    <div className="text-[10px] text-amber-300 mt-1">
                      โหลดไม่ครบ ({pct}%) — กด "โหลด" ที่ตอนนี้อีกครั้งเพื่อโหลดต่อ
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleDelete(ep)}
                    title="ลบออกจากเครื่อง"
                    className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-900/60 text-slate-400 hover:text-rose-300 border border-slate-700"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={!complete}
                    onClick={() => setPlaying(ep)}
                    className="px-2.5 py-1.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 disabled:opacity-40 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow"
                  >
                    <Play className="w-3 h-3 fill-current" />
                    <span>ดูในแอป</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {storage && storage.quota > 0 && (
        <div className="text-[10px] text-slate-500 flex items-center gap-1">
          <HardDrive className="w-3 h-3" />
          <span>
            พื้นที่ที่เว็บนี้ใช้ในเครื่อง {formatBytes(storage.usage)} จากที่ใช้ได้ประมาณ {formatBytes(storage.quota)}
          </span>
        </div>
      )}

      {playing && (
        <OfflinePlayerModal episode={playing} onClose={() => setPlaying(null)} onFinished={onWatched} />
      )}
    </div>
  );
};
